import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon";
import { distance } from "@turf/distance";
import { bearingToAzimuth, point, polygon } from "@turf/helpers";
import { getCoord } from "@turf/invariant";
import { rhumbBearing } from "@turf/rhumb-bearing";
import { rhumbDestination } from "@turf/rhumb-destination";

import i18n from "@/app/i18n";
import { LatLng, type Poi } from "@/libs/models";
import type { Geofence } from "@/libs/models/geofence";

export const clamp = (
  x: number,
  min: number | null,
  max: number | null,
): number => {
  return min != null && x < min ? min : max != null && x > max ? max : x;
};

/**
 * Calculates the coordinates of a square with a specified side length, whose sides are aligned with the bearing between
 * two given geospatial points.
 * The square is formed by displacing the provided distance in both directions from the initial points and obtaining the
 * coordinates of the four vertices.
 * In geojson first and last coordinates must be the same to close the polygon.
 */

export function getSquareCoordinates(
  [longitude1, latitude1]: any,
  [longitude2, latitude2]: any,
  distance: number,
): number[][] {
  // Create geospatial points from the coordinates
  const originCoordsA = point([longitude1, latitude1]);
  const originCoordsB = point([longitude2, latitude2]);

  // Calculate the bearing between the two points
  const bearing = bearingToAzimuth(rhumbBearing(originCoordsA, originCoordsB));

  // Calculate points displaced in both directions from points 1 and 2
  const upperLeft = rhumbDestination(originCoordsA, distance, bearing + 90, {
    units: "meters",
  });
  const upperRight = rhumbDestination(originCoordsB, distance, bearing + 90, {
    units: "meters",
  });
  const bottomLeft = rhumbDestination(originCoordsA, distance, bearing - 90, {
    units: "meters",
  });
  const bottomRight = rhumbDestination(originCoordsB, distance, bearing - 90, {
    units: "meters",
  });

  // Form a square with the coordinates in the correct order
  return [
    getCoord(bottomLeft),
    getCoord(bottomRight),
    getCoord(upperRight),
    getCoord(upperLeft),
    getCoord(bottomLeft),
  ];
}

export const degreesToRadians = (degrees: number) => {
  return (degrees * Math.PI) / 180;
};

export const radiansToDegrees = (radians: number) => {
  return (radians * 180) / Math.PI;
};

export const haversine = (
  coordA: { lat: number; lng: number },
  coordB: { lat: number; lng: number },
) => {
  const radiusEarth = 6371;

  const latPointA = coordA.lat;
  const latPointB = coordB.lat;
  const lngPointA = coordA.lng;
  const lngPointB = coordB.lng;

  const latDistance = degreesToRadians(latPointB - latPointA);
  const lngDistance = degreesToRadians(lngPointB - lngPointA);

  const sinA =
    Math.sin(latDistance / 2) * Math.sin(latDistance / 2) +
    Math.cos(degreesToRadians(latPointA)) *
      Math.cos(degreesToRadians(latPointB)) *
      Math.sin(lngDistance / 2) *
      Math.sin(lngDistance / 2);

  const cosC = 2 * Math.atan2(Math.sqrt(sinA), Math.sqrt(1 - sinA));
  const distanceInKm = radiusEarth * cosC;
  const distanceInMeters = distanceInKm * 1000;

  return distanceInMeters;
};

export const bearingBetweenCoordinates = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number => {
  const lat1Rad = degreesToRadians(lat1);
  const lng1Rad = degreesToRadians(lng1);
  const lat2Rad = degreesToRadians(lat2);
  const lng2Rad = degreesToRadians(lng2);

  const diffLng = lng2Rad - lng1Rad;

  const x = Math.sin(diffLng) * Math.cos(lat2Rad);
  const y =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(diffLng);
  const bearing = (radiansToDegrees(Math.atan2(x, y)) + 360) % 360;

  return bearing;
};

export const angleTo360Range = (angle: number): number => {
  angle = angle % 360;
  if (angle < 0) angle += 360;
  angle = Math.abs(angle);
  return angle;
};

export const rotation = (from: number, to: number) => {
  from = angleTo360Range(from);
  to = angleTo360Range(to);

  const unsafeRotation = to - from;
  let safeRotation = unsafeRotation % 360;
  if (safeRotation > 180) safeRotation = safeRotation - 360;
  if (safeRotation < -180) safeRotation = 360 + safeRotation;

  return safeRotation;
};

class CoordinateCartesianEquivalence {
  private m1: number;
  private m2: number;
  private m3: number;
  private m4: number;
  private p1: number;
  private p2: number;
  private p3: number;
  private zeroLat: number;
  private zeroLong: number;
  private metersPerDegreeLongitude: number;
  private metersPerDegreeLatitude: number;

  constructor(
    public center: any,
    width: number,
    public height: number,
  ) {
    const pi = Math.PI;

    this.m1 = 111132.92; // latitude calculation term 1
    this.m2 = -559.82; // latitude calculation term 2
    this.m3 = 1.175; // latitude calculation term 3
    this.m4 = -0.0023; // latitude calculation term 4
    this.p1 = 111412.84; // longitude calculation term 1
    this.p2 = -93.5; // longitude calculation term 2

    this.p3 = 0.118; // longitude calculation term 3
    this.zeroLat = 0;
    this.zeroLong = 0;
    this.metersPerDegreeLongitude = 0;
    const latitudeRadians = center.lat * (pi / 180);

    // alto
    this.metersPerDegreeLatitude =
      this.m1 +
      this.m2 * Math.cos(2 * latitudeRadians) +
      this.m3 * Math.cos(4 * latitudeRadians) +
      this.m4 * Math.cos(6 * latitudeRadians);

    // ancho
    this.metersPerDegreeLongitude =
      this.p1 * Math.cos(latitudeRadians) +
      this.p2 * Math.cos(3 * latitudeRadians) +
      this.p3 * Math.cos(5 * latitudeRadians);

    const latDiffDegrees = height / 2.0 / this.metersPerDegreeLatitude;
    this.zeroLat = center.lat - latDiffDegrees;

    const longDiffDegrees =
      width / 2.0 / (this.metersPerDegreeLongitude * Math.cos(latDiffDegrees));
    this.zeroLong = center.lng - longDiffDegrees;
  }

  toCartesianCoordinateNoRotation(coordinate: { lng: number; lat: number }) {
    const x = (coordinate.lng - this.zeroLong) * this.metersPerDegreeLongitude;
    const y = (coordinate.lat - this.zeroLat) * this.metersPerDegreeLatitude;

    return {
      lat: coordinate.lat,
      lng: coordinate.lng,
      x: x,
      y: y,
    };
  }

  toCoordinateNoRotation(cartesian: { x: number; y: number }) {
    const lat = this.zeroLat + cartesian.y / this.metersPerDegreeLatitude;
    const lng = this.zeroLong + cartesian.x / this.metersPerDegreeLongitude;

    return {
      lat: lat,
      lng: lng,
      x: cartesian.x,
      y: cartesian.y,
    };
  }
}

class CoordinateConverter {
  private earthMetricEq;
  private center;

  constructor(
    center: { lat: number; lng: number },
    public width: number,
    public height: number,
    public rotation: number,
  ) {
    this.width = width;
    this.height = height;
    this.rotation = rotation;
    this.earthMetricEq = new CoordinateCartesianEquivalence(
      center,
      width,
      height,
    );
    this.center = this.earthMetricEq.toCartesianCoordinateNoRotation(center);
  }

  toCartesian({ lat, lng }: { lat: number; lng: number }) {
    const nonRotated = this.earthMetricEq.toCartesianCoordinateNoRotation({
      lat,
      lng,
    });
    return this.rotate(nonRotated, this.rotation);
  }

  toCoordinate({ x, y }: { x: number; y: number }) {
    const rotatedCartesian = this.rotate({ x, y }, -this.rotation);

    return this.earthMetricEq.toCoordinateNoRotation(rotatedCartesian);
  }

  rotate(cartesian: { x: number; y: number }, radians: number) {
    const ox = this.center.x;
    const oy = this.center.y;
    const px = cartesian.x;
    const py = cartesian.y;
    const qx =
      ox + Math.cos(radians) * (px - ox) - Math.sin(radians) * (py - oy);
    const qy =
      oy + Math.sin(radians) * (px - ox) + Math.cos(radians) * (py - oy);
    return {
      x: qx,
      y: qy,
    };
  }
}

export default CoordinateConverter;

export class CoordinateConverterFlatEarth {
  private xAxis: { lat: number; lng: number };
  private yAxis: { lat: number; lng: number };
  private xNorm: number;
  private yNorm: number;

  constructor(
    private bottomRightCorner: LatLng,
    private bottomLeftCorner: LatLng,
    private topLeftCorner: LatLng,
    private topRightCorner: LatLng,
    private width: number,
    private length: number,
  ) {
    // Compute normalized X (horizontal) and Y (vertical) vectors
    this.xAxis = {
      lat: this.bottomRightCorner.lat - this.bottomLeftCorner.lat,
      lng: this.bottomRightCorner.lng - this.bottomLeftCorner.lng,
    };
    this.yAxis = {
      lat: this.topLeftCorner.lat - this.bottomLeftCorner.lat,
      lng: this.topLeftCorner.lng - this.bottomLeftCorner.lng,
    };

    // Compute norms (lengths) for normalization
    this.xNorm = Math.sqrt(this.xAxis.lat ** 2 + this.xAxis.lng ** 2);
    this.yNorm = Math.sqrt(this.yAxis.lat ** 2 + this.yAxis.lng ** 2);

    // Normalize the basis vectors
    this.xAxis.lat /= this.xNorm;
    this.xAxis.lng /= this.xNorm;
    this.yAxis.lat /= this.yNorm;
    this.yAxis.lng /= this.yNorm;
  }

  private roundTo(num: number, decimals = 6): number {
    const value = Number(num.toFixed(decimals));
    return Object.is(value, -0) ? 0 : value;
  }

  /**
   * Converts a Cartesian coordinate (x, y) into a geographic coordinate (latitude, longitude)
   * using bilinear interpolation.
   */
  toCoordinate(cartesian: { x: number; y: number }): LatLng {
    // Compute scale factors dynamically using all four corners
    const xFactor = cartesian.x / this.width;
    const yFactor = cartesian.y / this.length;

    // Interpolate between bottom and top edges based on yFactor
    const latBottom =
      this.bottomLeftCorner.lat +
      xFactor * (this.bottomRightCorner.lat - this.bottomLeftCorner.lat);
    const lngBottom =
      this.bottomLeftCorner.lng +
      xFactor * (this.bottomRightCorner.lng - this.bottomLeftCorner.lng);

    const latTop =
      this.topLeftCorner.lat +
      xFactor * (this.topRightCorner.lat - this.topLeftCorner.lat);
    const lngTop =
      this.topLeftCorner.lng +
      xFactor * (this.topRightCorner.lng - this.topLeftCorner.lng);

    // Compute final lat/lng based on yFactor
    const lat = latBottom + yFactor * (latTop - latBottom);
    const lng = lngBottom + yFactor * (lngTop - lngBottom);

    return new LatLng(lat, lng);
  }

  /**
   * Converts a geographic coordinate (latitude, longitude) into a Cartesian coordinate (x, y)
   * by inverting the bilinear interpolation using an iterative Newton's method.
   *
   * @param coordinate - The geographic coordinate {lat, lng}.
   * @returns A Cartesian coordinate {x, y}.
   */
  toCartesian(coordinate: { lat: number; lng: number }): {
    x: number;
    y: number;
  } {
    // Precompute constants for bilinear inversion.
    // Represent the bilinear mapping as:
    //   f(u,v) = bottomLeft + u*A + v*B + u*v*D,
    // where:
    const lat0 = this.bottomLeftCorner.lat;
    const lng0 = this.bottomLeftCorner.lng;

    const A_lat = this.bottomRightCorner.lat - this.bottomLeftCorner.lat;
    const B_lat = this.topLeftCorner.lat - this.bottomLeftCorner.lat;
    const D_lat = this.topRightCorner.lat - this.topLeftCorner.lat - A_lat;

    const A_lng = this.bottomRightCorner.lng - this.bottomLeftCorner.lng;
    const B_lng = this.topLeftCorner.lng - this.bottomLeftCorner.lng;
    const D_lng = this.topRightCorner.lng - this.topLeftCorner.lng - A_lng;

    // Target values
    const targetLat = coordinate.lat;
    const targetLng = coordinate.lng;

    // Initial guess for u and v (unit coordinates)
    let u = 0.5,
      v = 0.5;
    const maxIter = 20;
    const tol = 1e-6;

    for (let i = 0; i < maxIter; i++) {
      // Compute current mapping f(u,v)
      const f1 = lat0 + u * A_lat + v * B_lat + u * v * D_lat - targetLat;
      const f2 = lng0 + u * A_lng + v * B_lng + u * v * D_lng - targetLng;

      // Compute Jacobian matrix elements
      const J11 = A_lat + v * D_lat;
      const J12 = B_lat + u * D_lat;
      const J21 = A_lng + v * D_lng;
      const J22 = B_lng + u * D_lng;

      const det = J11 * J22 - J12 * J21;
      if (Math.abs(det) < 1e-12) {
        break; // Jacobian is nearly singular, abort iteration
      }

      // Solve for the updates (du, dv) using Cramer's rule
      const du = (-f1 * J22 + f2 * J12) / det;
      const dv = (-f2 * J11 + f1 * J21) / det;

      u += du;
      v += dv;

      if (Math.sqrt(du * du + dv * dv) < tol) {
        break; // Converged
      }
    }

    // Convert unit coordinates back to Cartesian
    const x = u * this.width;
    const y = v * this.length;

    // Round final result to ignore minor drifts
    return { x: this.roundTo(x), y: this.roundTo(y) };
  }
}

/**
 * Transforms cartesian coordinates (x, y) to geographic coordinates (latitude, longitude) based on the
 * provided building corner coordinates and its physical dimensions. This function ensures alignment with
 * the geographic coordinate system used in Situm Dashboard (on the contrary, SDKs/LibRoutes use a different
 * transform, hence introducing representation errors)
 *
 * @param {number} x - The x-coordinate in the building's cartesian coordinate system.
 * @param {number} y - The y-coordinate in the building's cartesian coordinate system.
 * @param {Object} bottomRightCorner - The geographic coordinates (latitude and longitude) of the building's image bottom right corner
 * @param {Object} bottomLeftCorner - The geographic coordinates (latitude and longitude) of the building's image bottom left corner
 * @param {Object} topLeftCorner - The geographic coordinates (latitude and longitude) of the building's image top left corner
 * @param {number} width - The total width of the building in meters.
 * @param {number} length - The total length of the building in meters.
 *
 * @returns {LatLng} The geographic coordinates (latitude and longitude) corresponding to the input cartesian coordinates,
 * transformed to align with the coordinate system used in the Situm Dashboard, ensuring consistency across platform representations.
 */

export function xyToLatLng(
  x: number,
  y: number,
  bottomRightCorner: LatLng,
  bottomLeftCorner: LatLng,
  topLeftCorner: LatLng,
  width: number,
  length: number,
): LatLng {
  //Compute the Lat/Lng vectors that go from the bottomLeftCorner to the bottomRightCorner
  // (corresponding with the cartesian X-axis of the building image)
  const XDiffLat = bottomRightCorner.lat - bottomLeftCorner.lat;
  const XDiffLng = bottomRightCorner.lng - bottomLeftCorner.lng;
  //... and the topLeftCorner (cartesian Y-axis of the building image)
  const YDiffLat = topLeftCorner.lat - bottomLeftCorner.lat;
  const YDiffLng = topLeftCorner.lng - bottomLeftCorner.lng;

  //Between 0 and 1, how close is x or y to the maximum (width or length respectively)?
  const factorX = x / width;
  const factorY = y / length;

  //From the bottomLeftCorner, we add the Lat/Lng vectors scaled by the previously computed factors
  const newLat = bottomLeftCorner.lat + YDiffLat * factorY + XDiffLat * factorX;
  const newLng = bottomLeftCorner.lng + YDiffLng * factorY + XDiffLng * factorX;
  return new LatLng(newLat, newLng);
}

export const poiIsInsideGeofence = (poi: Poi, geofences: Array<Geofence>) => {
  let insideGeofence: Geofence | null = null;
  geofences?.forEach((geofence: any) => {
    if (
      !geofence.customFields.some(
        (cf: any) => cf.key == "use_in_map_viewer" && cf.value === "true",
      )
    ) {
      return;
    }

    try {
      const polygonGeometry = polygon([geofence.geometric as any]);
      const pointGeometry = point([
        poi.position.lat as number,
        poi.position.lng as number,
      ]);
      const inside = booleanPointInPolygon(pointGeometry, polygonGeometry);

      // If the poi is inside a geofence mark it
      if (inside) {
        insideGeofence = geofence;
      }
    } catch (e) {
      console.error("error calculating insideGeofence", e, geofence.geometric, [
        poi.position.lng,
        poi.position.lat,
      ]);
    }
  });

  return insideGeofence;
};

export const validateLatitude = (value: number | undefined) => {
  if (value != null && value >= -90 && value <= 90) {
    return value;
  }
  return null;
};

export const validateLongitude = (value: number | undefined) => {
  if (value != null && value >= -180 && value <= 180) {
    return value;
  }
  return null;
};

export function pixelToMeter(latitude: number, zoomLevel: number): number {
  return (156543.03392 * Math.cos((latitude * Math.PI) / 180)) / 2 ** zoomLevel;
}

export function calculateOcclusionDistanceForZoom(
  zoomLevel: number,
  maxZoom = 23,
  factor = 1,
) {
  // Define the ranges and slopes for each segment
  const segments = [
    { endDistance: 3000, maxZoom: 10, minZoom: 1, startDistance: 1000000 },
    { endDistance: 2000, maxZoom: 11, minZoom: 10, startDistance: 3000 },
    { endDistance: 1000, maxZoom: 12, minZoom: 11, startDistance: 2000 },
    { endDistance: 550, maxZoom: 13, minZoom: 12, startDistance: 1000 },
    { endDistance: 300, maxZoom: 14, minZoom: 13, startDistance: 550 },
    { endDistance: 125, maxZoom: 15, minZoom: 14, startDistance: 300 },
    { endDistance: 75, maxZoom: 16, minZoom: 15, startDistance: 125 },
    { endDistance: 68, maxZoom: 16.5, minZoom: 16, startDistance: 75 },
    { endDistance: 50, maxZoom: 17, minZoom: 16.5, startDistance: 68 },
    { endDistance: 33, maxZoom: 17.5, minZoom: 17, startDistance: 50 },
    { endDistance: 25, maxZoom: 18, minZoom: 17.5, startDistance: 33 },
    { endDistance: 12, maxZoom: 19, minZoom: 18, startDistance: 25 },
    { endDistance: 7, maxZoom: 20, minZoom: 19, startDistance: 12 },
    { endDistance: 2, maxZoom: 21, minZoom: 20, startDistance: 7 },
    { endDistance: 1, maxZoom: 22, minZoom: 21, startDistance: 2 },
    { endDistance: 1, maxZoom: 23, minZoom: 22, startDistance: 1 },
    // Add more segments as needed
  ];
  if (zoomLevel + 0.00001 >= maxZoom) return 1;

  // Find the appropriate segment for the given zoom level
  for (const segment of segments) {
    if (zoomLevel >= segment.minZoom && zoomLevel <= segment.maxZoom) {
      // Calculate distance using linear interpolation
      const slope =
        (segment.endDistance * factor - segment.startDistance * factor) /
        (segment.maxZoom - segment.minZoom);
      return (
        segment.startDistance * factor + slope * (zoomLevel - segment.minZoom)
      );
    }
  }

  // Default distance if zoom level is outside defined ranges
  return 1000;
}

/**
 * Adds a z-coordinate to a set of coordinates.
 *
 * @param {GeojsonGeometry} coords - The input coordinates.
 * @param {number} [z=0] - The z-coordinate to be added. Defaults to 0.
 * @returns {GeojsonGeometry<Coordinate3D>} The updated coordinates with the z-coordinate added.
 */
export const addZCoordinate = (
  coords: GeojsonGeometry,
  z: number = 0,
): GeojsonGeometry<Coordinate3D> => {
  // If all entries are numbers, return the lat lng components swapped
  if (coords.every((entry) => typeof entry === "number")) {
    return [coords[0], coords[1], z];
  }
  // Check if none of the entries are numbers (they are arrays)
  if (coords.every((entry) => typeof entry !== "number")) {
    return coords.map((coord) =>
      addZCoordinate(coord, z),
    ) as GeojsonGeometry<Coordinate3D>;
  }

  throw new Error(`Invalid input geometry: ${coords.toString()}`);
};

/**
 * Adds an offset to the x and y coordinates.
 *
 * @template T
 * @param {T} coords - The input coordinates.
 * @param {number} offset - The offset to be added to the lat and lng values.
 * @returns {T} The coordinates with the offset applied to the lat and lng values.
 */
export const addCoordinateOffset = <T extends GeojsonGeometry>(
  coords: T,
  offset: number | [number, number],
): T => {
  let lngLatOffset: [number, number];
  if (!Array.isArray(offset)) {
    lngLatOffset = [offset, offset];
  } else {
    lngLatOffset = offset;
  }

  // If all entries are numbers, return the lat lng components swapped
  if (coords.every((entry) => typeof entry === "number")) {
    const [first, second, ...rest] = coords;
    return [
      first + lngLatOffset[0],
      (second as number) + lngLatOffset[1],
      ...rest,
    ] as T;
  }
  // Check if none of the entries are numbers (they are arrays)
  if (coords.every((entry) => typeof entry !== "number")) {
    return coords.map((coord) => addCoordinateOffset(coord, lngLatOffset)) as T;
  }

  throw new Error(`Invalid input geometry: ${coords.toString()}`);
};

/**
 * Rounds the coordinate values to a specified number of decimal places.
 *
 * @template T
 * @param {T} coords - The input coordinates.
 * @param {number} decimals - The number of decimal places to round to.
 * @returns {T} The coordinates with values rounded to the specified number of decimal places.
 */
export const toFixedDecimalsCoordinate = <T extends GeojsonGeometry>(
  coords: T,
  decimals: number,
): T => {
  // If all entries are numbers, return the lat lng components swapped
  if (coords.every((entry) => typeof entry === "number")) {
    return coords.map((c: number) => parseFloat(c.toFixed(decimals))) as T;
  }
  // Check if none of the entries are numbers (they are arrays)
  if (coords.every((entry) => typeof entry !== "number")) {
    return coords.map((coord) =>
      toFixedDecimalsCoordinate(coord, decimals),
    ) as T;
  }

  throw new Error(`Invalid input geometry: ${coords.toString()}`);
};

/**
 * Swaps the latitude and longitude in a set of coordinates.
 *
 * @template T
 * @param {T} coords - The input coordinates.
 * @returns {T} The coordinates with latitude and longitude swapped.
 */
export const swapLatLngCoordinates = <T extends GeojsonGeometry>(
  coords: T,
): T => {
  // If all entries are numbers, return the lat lng components swapped
  if (coords.every((entry) => typeof entry === "number")) {
    const [first, second, ...rest] = coords as number[];
    return [second, first, ...rest] as T;
  }

  // Check if none of the entries are numbers (they are arrays)
  if (coords.every((entry) => typeof entry !== "number")) {
    return (coords as GeojsonGeometry[]).map(swapLatLngCoordinates) as T;
  }

  throw new Error(`Invalid input geometry: ${coords.toString()}`);
};

/**
 * Revolves a coordinate around a specified center point, by a given rotation in degrees
 *
 * @param {Coordinate2D} coordinate - The coordinate to rotate, specified as [lng, lat].
 * @param {Coordinate2D} center - The center of rotation, specified as [lng, lat].
 * @param {number} rotationDegrees - The rotation angle in degrees (clockwise).
 * @returns {Coordinate2D} The rotated coordinate, specified as [lng, lat].
 */
export const rotateCoordinate = (
  coordinate: Coordinate2D,
  center: Coordinate2D, // Rotation center [lng, lat]
  rotationDegrees: number, // Rotation in degrees
): Coordinate2D => {
  // Convert rotation to radians
  const rotationRadians = degreesToRadians(rotationDegrees);

  // Convert center to radians
  const [centerLng, centerLat] = center;
  const centerLatRad = degreesToRadians(centerLat);
  const centerLngRad = degreesToRadians(centerLng);

  // Convert lat/lng to radians
  const latRad = degreesToRadians(coordinate[1]);
  const lngRad = degreesToRadians(coordinate[0]);

  // Translate the point to the origin (relative to the center)
  const x = (lngRad - centerLngRad) * Math.cos(centerLatRad);
  const y = latRad - centerLatRad;

  // Apply the rotation
  const xRot = x * Math.cos(rotationRadians) - y * Math.sin(rotationRadians);
  const yRot = x * Math.sin(rotationRadians) + y * Math.cos(rotationRadians);

  // Translate the point back
  const newLngRad = xRot / Math.cos(centerLatRad) + centerLngRad;
  const newLatRad = yRot + centerLatRad;

  // Convert the result back to degrees
  const newLat = radiansToDegrees(newLatRad);
  const newLng = radiansToDegrees(newLngRad);

  return [newLng, newLat];
};

type ObjectWithLatLng = {
  id: string | number;
  lat: number;
  lng: number;
};

/**
 * Find the nearest object in a list of objects with lat/lng properties.
 *
 * @param lat - The latitude of the reference point.
 * @param lng - The longitude of the reference point.
 * @param list - The list of objects to search through.
 *
 * @returns The nearest object in the list, or null if the list is empty.
 **/
export function findNearestObject<T extends ObjectWithLatLng>(
  lat: number,
  lng: number,
  list: T[],
): T | null {
  const refPoint = point([lng, lat]); // Ojo, turf usa [lng, lat]

  let nearestPoint = null;
  let minDistance = Infinity;

  list.forEach((o) => {
    const puntoObjeto = point([o.lng, o.lat]);
    const dist = distance(refPoint, puntoObjeto, {
      units: "kilometers",
    });

    if (dist < minDistance) {
      minDistance = dist;
      nearestPoint = o;
    }
  });

  return nearestPoint;
}
